import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster, toast } from 'sonner';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2, Users, Shield, Lightbulb, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSuccess(true);
        toast.success('Message sent successfully! We\'ll be in touch soon.');
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const engagementItems = [
    'Discovery sessions on your current PR, diligence, and integration workflows.',
    'Architecture and security deep dives with technical reviewers.',
    'Pilot and implementation planning tailored to your environment.'
  ];

  const helpfulToShare = [
    'Whether you\'re planning an RFI/RFP, pilot, or internal exploration.',
    'Any specific programs, portfolios, or acquisition workflows you\'d like to discuss.',
    'Who should be in the room (e.g., contracting, program, security, IT).'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <span className="inline-block text-sm font-semibold uppercase tracking-widest text-gray-600 mb-4">
                  Connect with the team behind AcuSightPro
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Let&apos;s talk about{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    your acquisition workflow.
                  </span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Whether you&apos;re exploring a pilot, planning for an RFI or RFP, or just want to see how AcuSightPro could support your portfolio, we&apos;d be glad to connect.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-8">
                  <a
                    href="#contact-form"
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                  >
                    Send us a message
                  </a>
                  <a
                    href="/tech-security"
                    className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-all"
                  >
                    Review tech & security
                  </a>
                </div>
                
                <p className="text-sm text-gray-500 italic">
                  AcuSightPro is brought to you by Encore Services, LLC, a small business focused on helping federal acquisition teams modernize without starting from scratch.
                </p>
              </div>

              {/* Right Card - How we typically engage */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">How we typically engage</h3>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                    Encore Services
                  </span>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We meet agencies where they are, whether that&apos;s early exploration, architecture review, or preparing for a live procurement.
                </p>
                <ul className="space-y-4">
                  {engagementItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 mt-2 bg-purple-600 rounded-full"></span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & Info Section */}
        <section id="contact-form" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Get in touch with{' '}
                <span className="gold-wave-gradient-text">
                  Encore Services, LLC.
                </span>
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Use the form to reach the AcuSightPro team, or contact us directly using the information below. We can coordinate with contracting, program, security, and architecture stakeholders as needed.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Primary Contact Card */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Primary contact</h3>
                <p className="text-gray-600 mb-6">
                  AcuSightPro is delivered and supported by Encore Services, LLC.
                </p>

                {/* Company Info */}
                <div className="mb-8">
                  <h4 className="font-bold text-gray-900 mb-2">Encore Services, LLC</h4>
                  <div className="text-gray-600 space-y-1">
                    <p>9500 Medical Center Drive, Suite 300</p>
                    <p>Largo, MD 20774</p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <p className="text-gray-600">
                      Business Telephone:{' '}
                      <a href="tel:+12024608668" className="text-purple-600 hover:underline">(202) 460-8668</a>
                    </p>
                    <p className="text-gray-600">
                      General E-mail:{' '}
                      <a href="mailto:info@encoresvcsllc.com" className="text-purple-600 hover:underline">info@encoresvcsllc.com</a>
                    </p>
                  </div>
                </div>

                {/* Jeffrey Contact */}
                <div className="mb-8 pt-6 border-t border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-2">Jeffrey A. Woodson</h4>
                  <div className="text-gray-600 space-y-1">
                    <p>Encore Services, LLC</p>
                    <p>9500 Medical Center Drive, Suite 300</p>
                    <p>Largo, MD 20774</p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <p className="text-gray-600">
                      Business Telephone:{' '}
                      <a href="tel:+12024608668" className="text-purple-600 hover:underline">202-460-8668</a>
                    </p>
                    <p className="text-gray-600">
                      E-mail:{' '}
                      <a href="mailto:jwoodson@encoresvcsllc.com" className="text-purple-600 hover:underline">jwoodson@encoresvcsllc.com</a>
                    </p>
                  </div>
                </div>

                {/* Helpful to share */}
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-3">When you reach out, it&apos;s helpful to share:</h4>
                  <ul className="space-y-2">
                    {helpfulToShare.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-600">
                        <span className="flex-shrink-0 w-1.5 h-1.5 mt-2 bg-gray-400 rounded-full"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Contact Form Card */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                {isSuccess ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-6">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. We&apos;ll get back to you within 1-2 business days.
                    </p>
                    <button
                      onClick={() => setIsSuccess(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-full hover:bg-gray-300 transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Me</h3>
                    <p className="text-gray-600 text-sm mb-6">
                      Fields marked with an <span className="text-red-500">*</span> are required
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none resize-none"
                        />
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Brought to you by Encore Section */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Brought to you by{' '}
                <span className="gold-wave-gradient-text">
                  Encore Services, LLC.
                </span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed max-w-3xl">
                Encore pairs hands-on acquisition experience with modern product and engineering teams.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mt-2">
                AcuSightPro is our way of putting that combination directly into your day-to-day workflows.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Acquisition-first perspective */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Acquisition-first perspective</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Built around the lived reality of 1102s, branch chiefs, and HCA-level leadership.
                </p>
                <p className="text-gray-600">
                  We&apos;ve designed AcuSightPro to feel like a system of record for deals, not just another dashboard. The goal is to help your existing teams do more, with clearer risk visibility and less swivel-chair work.
                </p>
              </div>

              {/* Modern technology & delivery */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Modern technology & delivery</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Architecture, security, and AI features that can stand up to technical review.
                </p>
                <p className="text-gray-600">
                  From deployment options and integration patterns to AI governance and auditability, we expect your reviewers to ask hard questions. Our team is ready to walk through those details with you.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
